import React from "react";
import { NavLink } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import type { VisitDraftsApi } from "./useVisitDrafts";

interface UnsavedDraftsBarProps {
  drafts: VisitDraftsApi;
  /** Tab paths, already carrying the appointment query. */
  links: { treatments: string; prescriptions: string; plan: string };
}

/**
 * Pinned to the bottom of the treatment page while any tab holds unsaved
 * rows. One Save All for the whole visit; tab chips jump to the rows.
 */
export default function UnsavedDraftsBar({ drafts, links }: UnsavedDraftsBarProps) {
  const { counts, hasDrafts, isSaving, saveAll, discardAll } = drafts;
  if (!hasDrafts) return null;

  const chips: { label: string; count: number; to: string }[] = [
    { label: "Treatment", count: counts.treatments, to: links.treatments },
    { label: "Drugs", count: counts.prescriptions, to: links.prescriptions },
    { label: "Plan", count: counts.plan, to: links.plan },
  ].filter((chip) => chip.count > 0);

  const handleDiscard = () => {
    if (window.confirm(`Discard ${counts.total} unsaved item${counts.total === 1 ? "" : "s"}?`)) {
      discardAll();
    }
  };

  return (
    <div className="fixed bottom-3 left-1/2 z-40 -translate-x-1/2 px-3 w-full max-w-3xl pointer-events-none">
      <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/95 px-3 py-2 shadow-lg shadow-amber-200/40 backdrop-blur dark:border-amber-700 dark:bg-amber-900/80 dark:shadow-black/40">
        <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
          {counts.total} unsaved
        </span>
        <span className="hidden sm:inline text-amber-300 dark:text-amber-600">·</span>
        <div className="flex flex-wrap items-center gap-1">
          {chips.map((chip) => (
            <NavLink
              key={chip.label}
              to={chip.to}
              className={({ isActive }) =>
                `rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${
                  isActive
                    ? "bg-amber-600 text-white"
                    : "bg-white text-amber-800 hover:bg-amber-100 dark:bg-amber-800/60 dark:text-amber-100 dark:hover:bg-amber-700"
                }`
              }
            >
              {chip.label} {chip.count}
            </NavLink>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleDiscard}
            disabled={isSaving}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-white/70 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-amber-800/60"
          >
            <DeleteSweepIcon sx={{ fontSize: 15 }} />
            Discard All
          </button>
          <button
            type="button"
            onClick={() => void saveAll()}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight px-3 py-1.5 text-xs font-semibold text-white shadow hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50"
          >
            <SaveIcon sx={{ fontSize: 15 }} />
            {isSaving ? "Saving..." : "Save All"}
          </button>
        </div>
      </div>
    </div>
  );
}
